import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex, Grid, Section, Input, Select, PasswordInput
} from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import BaseFormV2 from 'react/containers/layout/Crud/BaseFormV2';
import { getResourceTo } from 'react/utils/utils';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import queryClient from 'react/services/queryClient';
import { useAuthCredentials } from 'react/queries/AuthCredentials';

export const AuthMethods = {
  NOAUTH: 'NOAUTH',
  BASIC: 'BASIC',
  BEARER_TOKEN: 'BEARER',
  API_KEY: 'APIKEY',
  OAUTH: 'OAUTH2'
};

function AuthorizationCredentialForm({ id }) {
  const [authMethod, setAuthMethod] = useState(AuthMethods.NOAUTH);

  const { data = [], isFetching } = useAuthCredentials();

  const authenticationOptions = [
    {
      label: getResourceTo('authCredentials.methods.noAuth'),
      value: AuthMethods.NOAUTH
    },
    {
      label: getResourceTo('authCredentials.methods.basicAuth'),
      value: AuthMethods.BASIC
    },
    {
      label: getResourceTo('authCredentials.methods.bearerToken'),
      value: AuthMethods.BEARER_TOKEN
    },
    {
      label: getResourceTo('authCredentials.methods.apiKey'),
      value: AuthMethods.API_KEY
    },
    {
      label: getResourceTo('authCredentials.methods.OAuth'),
      value: AuthMethods.OAUTH
    }
  ];

  const parsePayload = (values) => {
    const payload = {
      name: values.name,
      type: values.type,
      params: {}
    };

    const getPayloadByMethod = {
      [AuthMethods.BASIC]: () => {
        const { username, password } = values;

        Object.assign(payload, {
          params: {
            username, password
          }
        });

        return payload;
      },
      [AuthMethods.API_KEY]: () => {
        const { key, value, useAsQueryParam } = values;

        Object.assign(payload, {
          params: {
            key,
            value,
            useAsQueryParam
          }
        });

        return payload;
      },
      [AuthMethods.BEARER_TOKEN]: () => {
        const { token } = values;

        Object.assign(payload, {
          params: {
            token
          }
        });

        return payload;
      },
      [AuthMethods.OAUTH]: () => {
        const {
          tokenEndpoint,
          clientId,
          clientSecret,
          scope,
          grantType
        } = values;

        Object.assign(payload, {
          params: {
            tokenEndpoint,
            clientId,
            clientSecret,
            scope,
            grantType
          }
        });

        return payload;
      }
    };

    return getPayloadByMethod[authMethod]?.() ?? payload;
  };

  const handleSubmit = async (values, isEdit) => {
    const {
      name,
      params,
      type
    } = parsePayload(values);
    const method = isEdit ? 'PUT' : 'POST';
    const payload = !isEdit ? {
          name,
          params,
          type
    } : {
          id,
          name,
          type: {
            ...params,
            engineType: type
          }
        };

    const response = await defaultFetch('AuthConfig', payload, method);

    return response;
  };

  const isEdit = useMemo(() => !!id, [id]);

  const isLoadingForm = useMemo(() => {
    if (!isEdit) return false;

    if (isEdit) return isFetching;

    return false;
  }, [isEdit, isFetching]);

  const requiredFields = useMemo(() => {
    const baseFields = ['name', 'type'];

    switch (true) {
      case authMethod === AuthMethods.NOAUTH:
        return baseFields;
      case authMethod === AuthMethods.BASIC:
        return baseFields.concat(['username', 'password']);
      case authMethod === AuthMethods.BEARER_TOKEN:
        return baseFields.concat(['token']);
      case authMethod === AuthMethods.API_KEY:
        return baseFields.concat(['key', 'value', 'useAsQueryParam']);
      case authMethod === AuthMethods.OAUTH: {
        if (!isEdit) {
          baseFields.push('clientSecret');
        }

        return baseFields.concat(['tokenEndpoint', 'clientId', 'scope', 'grantType']);
      }
      default:
        return baseFields;
    }
  }, [authMethod, isEdit]);

  const initialData = useMemo(() => {
    const findRegister = data.find((item) => item.id === id);

    if (!findRegister) return {};

    const {
      name,
      type: {
        engineType,
        ...params
      }
    } = findRegister;

    return {
      name,
      type: engineType,
      ...params
    };
  }, [data, id]);

  useEffect(() => {
    if (Object.keys(initialData).length && !isFetching) {
      setAuthMethod(initialData.type);
    }
  }, [initialData, isFetching]);

  return (
    <BaseFormV2
      id={id}
      entity="AuthorizationCredential"
      defaultValues={initialData}
      isLoading={isLoadingForm}
      saveAction={handleSubmit}
      requiredFields={requiredFields}
      afterSubmit={() => {
        queryClient.invalidateQueries(['auth-credentials']);
      }}
    >
      <Flex
        flexDirection="column"
        gap={16}
        dataTestId="authorization-credential-form"
      >
        <Section label="General information" hasExpandAction={false}>
          <Grid
            templateColumns="0.4fr"
            breakpoints={{
              md: {
                templateColumns: '0.5fr'
              }
            }}
            gap={16}
          >
            <Flex gap={16}>
              <Input
                dataTestId="credential-name"
                label={getResourceTo('authCredentials.fields.description')}
                name="name"
                required
              />
              <Select
                dataTestId="credential-method"
                label={getResourceTo('authCredentials.fields.method')}
                name="type"
                options={authenticationOptions}
                onChange={(option) => setAuthMethod(option.value)}
                required
              />
            </Flex>

            <Flex
              shouldShow={[authMethod, initialData?.type].includes(
                AuthMethods.BASIC
              )}
              gap={16}
            >
              <Input
                dataTestId="credential-username"
                label={getResourceTo('authCredentials.fields.username')}
                name="username"
                required
              />
              <PasswordInput
                dataTestId="credential-password"
                label={getResourceTo('authCredentials.fields.password')}
                name="password"
                required
              />
            </Flex>

            <Flex
              shouldShow={[authMethod, initialData?.type].includes(
                AuthMethods.BEARER_TOKEN
              )}
              gap={16}
            >
              <Input
                dataTestId="credential-bearer-token"
                label={getResourceTo('authCredentials.fields.token')}
                name="token"
                required
              />
            </Flex>

            <Flex
              shouldShow={[authMethod, initialData?.type].includes(
                AuthMethods.API_KEY
              )}
              flexDirection="column"
              gap={16}
            >
              <Flex gap={16}>
                <Input
                  dataTestId="credential-key"
                  label={getResourceTo('authCredentials.fields.key')}
                  name="key"
                  required
                />
                <Input
                  dataTestId="credential-value"
                  label={getResourceTo('authCredentials.fields.value')}
                  name="value"
                  required
                />
              </Flex>
              <Select
                dataTestId="credential-use-as-query-param"
                label={getResourceTo('authCredentials.fields.addApiKeyTo')}
                name="useAsQueryParam"
                options={[
                  {
                    label: getResourceTo(
                      'authCredentials.fields.apiKey.header'
                    ),
                    value: false
                  },
                  {
                    label: 'Query Params',
                    value: true
                  }
                ]}
              />
            </Flex>

            <Flex
              shouldShow={[authMethod, initialData?.type].includes(
                AuthMethods.OAUTH
              )}
              flexDirection="column"
              gap={16}
            >
              <Flex gap={16}>
                <Input
                  dataTestId="credential-token-end-point"
                  label={getResourceTo('authCredentials.fields.tokenURL')}
                  name="tokenEndpoint"
                  required
                />
                <Input
                  dataTestId="credential-client-id"
                  label={getResourceTo('authCredentials.fields.clientId')}
                  name="clientId"
                  required
                />
                <Input
                  dataTestId="credential-client-secret"
                  label={getResourceTo('authCredentials.fields.clientSecret')}
                  name="clientSecret"
                  required={!isEdit}
                />
              </Flex>
              <Flex gap={16}>
                <Input
                  dataTestId="credential-scope"
                  label={getResourceTo('authCredentials.fields.scope')}
                  name="scope"
                  withHelpText={getResourceTo("authCredentials.fields.scope.helpText")}
                  required
                />
                <Input
                  dataTestId="credential-grant-type"
                  label={getResourceTo('authCredentials.fields.grantType')}
                  withHelpText={getResourceTo('authCredentials.fields.grantType.helpText')}
                  name="grantType"
                  required
                />
              </Flex>
            </Flex>
          </Grid>
        </Section>
      </Flex>
    </BaseFormV2>
  );
}

AuthorizationCredentialForm.propTypes = {
  id: PropTypes.string
};

export default AuthorizationCredentialForm;
